import { useState, useEffect, useCallback } from "react";
import { Users } from "lucide-react";

import axiosInstance from "src/common/axiosInstance";

import OwnersHeader from "./OwnersHeader";
import OwnersCardsCarousel from "./OwnersCardsCarousel";
import LeadOwnersModal from "./LeadOwnersModal/LeadOwnersModal";

export default function LeadOwnersPanel({ leadId, workspaceId }) {
  const [loading, setLoading] = useState(true);
  const [owners, setOwners] = useState([]);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(true);

  const fetchOwners = useCallback(async () => {
    try {
      const res = await axiosInstance.get(
        `/leads/${leadId}/owners`
      );

      const formatted = (res.data.owners || []).map((owner) => ({
        user_id: owner.user.user_id || owner.user.id,
        user: owner.user,
        stage_ids: owner.stage_ids || [],
        isNew: false,
      }));

      setOwners(formatted);
    } catch (err) {
      console.error(err);
      throw err;
    }
  }, [leadId]);

  async function fetchStages() {
    try {
      setLoadingStages(true);

      const selectedPipelineId = localStorage.getItem("selectedPipelineId");

      const res = await axiosInstance.get(
        `/admin/pipelines/stages?pipeline_id=${selectedPipelineId}`
      );

      setStages(Array.isArray(res.data) ? res.data : res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStages(false);
    }
  }

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await Promise.all([
        fetchOwners(),
        fetchStages(),
      ]);
    } catch (err) {
      setError("Failed to load lead owners.");
    } finally {
      setLoading(false);
    }
  }, [fetchOwners]);

  useEffect(() => {
    if (leadId) {
      loadData();
    }
  }, [leadId, loadData]);

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const showEmptyState =
    !loading &&
    !error &&
    owners.length === 0;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">

      <OwnersHeader
        setIsModalOpen={setIsModalOpen}
      />

      <div className="mt-4 border-t border-slate-100 pt-4">

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
            {error}
          </div>
        )}

        {!error && loading && (
          <div className="flex items-center justify-center py-6 text-slate-500">
            Loading owners...
          </div>
        )}

        {!error && showEmptyState && (
          <div className="flex flex-col items-center justify-center gap-2 rounded-xl bg-slate-50 px-6 py-6 text-center">

            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-theme-50">
              <Users className="h-5 w-5 text-theme-500" />
            </div>

            <h3 className="text-base font-semibold text-slate-900">
              No Owners Assigned
            </h3>

            <p className="max-w-xs text-sm text-slate-500">
              No lead owners have been assigned yet.
            </p>

          </div>
        )}

        {!error && !loading && owners.length > 0 && (
          <OwnersCardsCarousel
            owners={owners}
            stages={stages}
          />
        )}

      </div>

      <LeadOwnersModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        leadId={leadId}
        workspaceId={workspaceId}
        owners={owners}
        setOwners={setOwners}
        fetchOwners={fetchOwners}
        stages={stages}
        loadingStages={loadingStages}
      />

    </section>
  );
}
